import {createContext, useContext, useState, useEffect} from 'react'; 
import Swal from 'sweetalert2';

const AuthContext = createContext(); 

export const useAuth = () => { 
    const context = useContext(AuthContext); 
    if (!context) { 
        throw new Error('useAuth debe estar dentro de AuthProvider'); 
    }
    return context; 
}

export const AuthProvider = ({children}) => { 
    const [isAuthenticated, setIsAuthenticated] = useState(false); 
    const [userData, setUserData] = useState(null); 
    const [loading, setLoading] = useState(true); 

    //Verificar token expirado. 
    const isTokenExpired = (token) => { 
        if (!token) return true; 

        try { 
            const payload = JSON.parse(atob(token.split('.')[1])); 
            return payload.exp < (Date.now() / 1000); 
        } catch (error) { 
            console.error('Error verificando expiración del token: ', error); 
            return true; 
        }
    }; 

    //Limpieza de autenticación. 
    const clearAuth = () => { 
        localStorage.removeItem('authToken'); 
        localStorage.removeItem('user'); 
        localStorage.removeItem('userType'); 
        setIsAuthenticated(false); 
        setUserData(null); 
    }; 

    // Manejo de errores de autenticación
    const handleAuthError = (errorData, status) => {
        let alertConfig = {
            title: 'Error de Autenticación',
            text: 'Hubo un problema con la autenticación',
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#242b57',
            customClass: {
                popup: 'popupbonito',
            },
            backdrop: true,
            allowOutsideClick: false
        };

        if (status === 401) {
            switch (errorData.code) {
                case 'TOKEN_EXPIRED':
                    alertConfig = {
                        ...alertConfig,
                        title: 'Sesión Expirada',
                        text: 'Tu sesión ha expirado. Serás redirigido al login.',
                        icon: 'warning',
                        timer: 3000,
                        timerProgressBar: true,
                        showConfirmButton: false, 
                        customClass: {
                            popup: 'popupbonito',
                        },
                    };
                    break;
                    
                case 'INVALID_TOKEN':
                    alertConfig = {
                        ...alertConfig,
                        title: 'Token Inválido',
                        text: 'El token de acceso no es válido. Por favor, inicia sesión nuevamente.',
                        icon: 'error',
                        customClass: {
                            popup: 'popupbonito',
                        },
                    };
                    break;
                    
                case 'AUTH_ERROR':
                    alertConfig = {
                        ...alertConfig,
                        title: ' Error de Autenticación',
                        text: 'Hubo un error en la autenticación. Intenta nuevamente.',
                        icon: 'error', 
                        customClass: {
                            popup: 'popupbonito',
                        },
                    };
                    break;
                    
                default:
                    alertConfig.text = errorData.error || 'No se proporcionó token de acceso';
            }

            Swal.fire(alertConfig).then(() => {
                if (errorData.code === 'TOKEN_EXPIRED' || errorData.code === 'INVALID_TOKEN') {
                    clearAuth();
                    window.location.href = '/login';
                }
            });
        } else if (status === 403) {
            Swal.fire({
                title: 'Acceso Denegado',
                text: errorData.error || 'No tienes permisos para realizar esta acción',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57', 
                    customClass: {
                    popup: 'popupbonito',
                }
            });
        }
    };

    // Fetch con manejo automático de errores
    const authenticatedFetch = async (url, options = {}) => {
        const token = getToken();
        
        const defaultHeaders = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };

        const fetchOptions = {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers
            }
        };

        try {
            const response = await fetch(url, fetchOptions);
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                
                if (response.status === 401 || response.status === 403) {
                    handleAuthError(errorData, response.status);
                }
                
                const error = new Error(`HTTP Error: ${response.status}`);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }

            return response;
        } catch (error) {
            if (!error.status) {
                Swal.fire({
                    title: 'Error de Conexión',
                    text: 'No se pudo conectar al servidor. Verifica tu conexión a internet.',
                    icon: 'warning',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: {
                    popup: 'popupbonito',
                    },
                });
            }
            throw error;
        }
    };

    const updateUserData = async () => {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('user');

        if (!token || !userId || isTokenExpired(token)) {
            console.error('Token inválido o expirado al intentar actualizar datos');
            return false;
        }

        try {
            const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/perfil/usuario/${userId}`);
            const updatedUserData = await response.json();
            
            setUserData(updatedUserData);
            
            const storedUserType = localStorage.getItem('userType');
            if (!storedUserType || parseInt(storedUserType) !== updatedUserData.tipo) {
                localStorage.setItem('userType', updatedUserData.tipo.toString());
            }
            
            return true;
        } catch (error) {
            console.error('Error al actualizar datos del usuario:', error);
            return false;
        }
    };

    //Actualizar datos localmente
    const setUpdatedUserData = (newData) => {
        setUserData(prev => {
            const updated = { ...prev, ...newData };
            return updated;
        });
    };

    //Verifica autenticación. 
    const checkAuthentication = async () => { 
        const token = localStorage.getItem('authToken'); 
        const userId = localStorage.getItem('user'); 

        if (!token || !userId) { 
            clearAuth(); 
            setLoading(false); 
            return; 
        }
        if (isTokenExpired(token)) { 
            clearAuth(); 
            setLoading(false); 
            return; 
        }
        try { 
            const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/perfil/usuario/${userId}`);
            const userData = await response.json(); 
            setUserData(userData); 
            setIsAuthenticated(true); 
            
            const storedUserType = localStorage.getItem('userType');
            if (!storedUserType || parseInt(storedUserType) !== userData.tipo) {
                localStorage.setItem('userType', userData.tipo.toString());
            }
        } catch (error) { 
            console.error('Error: ', error); 
            clearAuth(); 
        }
        setLoading(false); 
    }; 

    const logout = () => { 
        clearAuth(); 
    }; 

    useEffect(() => { 
        checkAuthentication(); 
    }, []); 

    const getToken = () => localStorage.getItem('authToken');

    const value = { 
        isAuthenticated, 
        userData, 
        loading, 
        logout, 
        checkAuthentication,
        updateUserData,      
        setUpdatedUserData,
        getToken,
        authenticatedFetch,
        handleAuthError,
        getUserType: () => {
            if (userData?.tipo) {
                return userData.tipo;
            }
            const storedType = localStorage.getItem('userType');
            return storedType ? parseInt(storedType) : null;
        }, 
        isUserType: (type) => {
            const userType = userData?.tipo || parseInt(localStorage.getItem('userType'));
            return userType === type;
        },
        isAdmin: () => {
            const userType = userData?.tipo || parseInt(localStorage.getItem('userType'));
            return userType === 2;
        },
        isStudent: () => {
            const userType = userData?.tipo || parseInt(localStorage.getItem('userType'));
            return userType === 1;
        },
        isTutor: () => {
            const userType = userData?.tipo || parseInt(localStorage.getItem('userType'));
            return userType === 3;
        }
    }; 

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    ); 
};